import React, {Fragment, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import __, { wrap } from "lodash";
import moment from "moment";
import { FormControl as MuiFormControl, FormGroup as MuiFormGroup, Grid as MuiGrid, Box as MuiBox, Tooltip, IconButton, Icon, TextField, Table, TableHead, TableRow, TableCell, TableBody, Popover, CircularProgress, Checkbox} from "@material-ui/core";
import { LabelledPaper, MaskedNumber } from '../../../controls';
import { useAirportCosts, useCostCategories, useCostingSectors, useCostingServices } from '../../../services/costings';
import { useSettings } from '../../../services/settings';
import { buildSectorsAirportData, initialiseAirportCost, getCargoData, defaultCostFilters } from '../includes/costingDialogFunctions';
import {useComboValues} from "../../../services/settings";
import { CheckboxEditor } from '../../../controls';
import Icons from '../../../icons';
import { ReactComponent as ChecklistStatusIndicator } from '../../../assets/ChecklistStatusIndicator.svg';

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const LabelledBox = (props) => {
  return (
    <LabelledPaper 
      checkChildren={true}
      style={{display: "block", border: "solid 1px #b9b9b9" }}
      labelStyle={{background: "#f0f0f0"}}
      collapsible={false} 
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </LabelledPaper>
  )
}

const FormControl = (props) => {
  return (
    <MuiFormControl 
      checkChildren={true}
      style={{display: "block" }}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiFormControl>
  )
}

const Box = (props) => {
  return (
    <MuiBox 
      checkChildren={true}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiBox>
  )
}

const Grid = (props) => {
  return (
    <MuiGrid 
      checkChildren={true}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiGrid>
  )
}

const FormGroup = (props) => {
  return (
    <MuiFormGroup 
      checkChildren={true}
      style={{padding: "8px 8px 8px 8px"}}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiFormGroup>
  )
}

const LabelledValue = (props) => {
  const { classes, label, value, sx, displayProps, labelProps, valueProps, ...other } = props;
  return (
    <Box
      className={classes.labelledValue}
      sx={{
        display: 'inline-flex', 
        flexDirection: 'row',
        justifyContent: 'flex-start',
        ...sx,
      }}
      {...other}
    >
      <div key="label" className="label" {...labelProps}>{label}:</div>
      <div key="value" className="value" {...valueProps}><MaskedNumber displayType="text" value={value} {...displayProps}/></div>
    </Box>
  );
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const getCostRecordAirportsTotal = (service, costFilters) => {
  const costRecordAirports = service?.costRecordAirports||[];
  const {costCategoryUuid} = service;
  const airportFilters = costFilters?.groundCostDetails?.airports||[];
  let total = 0;
  costRecordAirports.forEach((costing, i) => {
    let inboundFilter = true;
    let outboundFilter = true;
    if (airportFilters && airportFilters.length > i) {
      const airport = airportFilters[i];
      if (airport) {
        if (__.has(airport, costCategoryUuid)) inboundFilter = airport[costCategoryUuid].in;
        if (__.has(airport, costCategoryUuid)) outboundFilter = airport[costCategoryUuid].out;
      }
    }
    if (inboundFilter) {
      total += costing.totalInboundCost||0;
    }
    if (outboundFilter) {
      total += costing.totalOutboundCost||0;
    }
  })

  if (total) {
    return (<MaskedNumber displayType="text" value={total} prefix="$" decimalScale={2} fixedDecimalScale={true} />);
  }
  return (<span>-</span>)
}

const StatusIndicator = (props) => {
  const {color, tooltip, classes} = props;
  if (color === "none") {
    return null;
  }
  return (    
    <div style={{position: "absolute", top: -2, right: 0}}>
      <Tooltip title={tooltip} classes={{tooltip: classes.costingTooltip}}>
        <div>
          <ChecklistStatusIndicator style={{fill: `${color||'#ff0000'}`, stroke: "#777777", width: 10, height: 10 }}/>
        </div>
      </Tooltip>
    </div>
  )
}

const CostingField = (props) => {
  const {airportCosting, index, costCategory, aircraftReg, queryClient, sector, cargoData, onChange, readOnly, classes, costingDate, filtersEnabled, filters, onFilterChanged} = props;
  const {airportCode, cost: unfiltereredCost, costBreakdown, quantity, changed, selected, defaultCost, defaultInboundCost, defaultOutboundCost, defaultSelected, defaultCostBreakdown, defaultQuantity, totalInboundCost, totalOutboundCost, error, airportCostFound, useCost} = (airportCosting||{error: {costingMissing: true}});
  const {type} = sector;
  const [anchorEl, setAnchorEl] = React.useState(null);
  let valueEntryType = costCategory?.valueEntryType;

  let cost = unfiltereredCost;
  if (!filters.in || !filters.out) {
    cost = 0;
    if (filters.in) {
      cost += totalInboundCost;
    }
    if (filters.out) {
      cost += totalOutboundCost;
    }
  }

  const comboKeys = ['COSTING UNIT TYPES'];
  const comboValues = useComboValues({queryClient, extraKeys: comboKeys, useCache: true});

  let fieldName = 'Quantity';
  let prefix = '';
  let suffix = '';
  let field = 'quantity';
  let decimalPlaces = 0;

  if (valueEntryType === null || __.isEmpty(valueEntryType)) valueEntryType = 'COST';

  if (valueEntryType === 'COST') {
    fieldName = 'Cost';
    prefix = '$';
    field = 'cost';
    decimalPlaces = 2;
  }

  if (comboValues && comboValues.data) {
    let option = __.find(comboValues.data, {code: valueEntryType});
    if (option) {
      fieldName = option.value;
      if (__.has(option.data, 'type')) field = option.data.type;
      if (__.has(option.data, 'prefix')) prefix = option.data.prefix;
      if (__.has(option.data, 'suffix')) suffix = option.data.suffix;
      if (__.has(option.data, 'decimalPlaces')) decimalPlaces = option.data.decimalPlaces;      
    }
  }

  const [fieldState, setFieldState] = useState({editing: false, selected, changed, quantity, cost, costBreakdown, recalculating: false, totalInboundCost, totalOutboundCost, error, airportCostFound, type, field, useCost: Boolean(useCost ?? false)});

  const buildCostDetails = (costBreakdown, usePercentageWidths) => {
    if (Array.isArray(costBreakdown) && costBreakdown.length > 0) {
    return (
        <div style={usePercentageWidths?{}:{display: "block", position: "relative", minWidth: 500}}>
          <div style={{fontWeight: 'bolder', textAlign: 'center'}}>Cost Breakdown</div>
          {costBreakdown.map(line => (
            <div>
              <div style={{display: 'inline-block', width: usePercentageWidths?'35%':150}}>{line.lineName}</div>
              <div style={{display: 'inline-block', width: usePercentageWidths?'40%':300, textOverflow: "wrap"}}>{line.calculationText}</div>
              <div style={{display: 'inline-block', width: usePercentageWidths?'25%':50, textAlign: 'right'}}>
                <MaskedNumber 
                  displayType="text"
                  value={line.lineCost} 
                  prefix="$" 
                  decimalScale={2} 
                  fixedDecimalScale={true} 
                />
              </div>
            </div>
          ))}
        </div>
      )
    } else {
      return null;
    }
  }

  const renderCostDetails = (costBreakdown, cost, defaultCost, isCost) => {
    const costDetails = buildCostDetails(costBreakdown, true);
    if (isCost) {
      return <>
        {costDetails}
        <div style={{borderTop: "1px solid #dddddd"}}>
          <div style={{display: 'inline-block', width: '35%'}}>Default Total:</div>
          <div style={{display: 'inline-block', width: '40%'}}></div>
          <div style={{display: 'inline-block', width: '25%', textAlign: 'right'}}>
            <MaskedNumber 
              displayType="text"
              value={defaultCost} 
              prefix="$" 
              decimalScale={2} 
              fixedDecimalScale={true} 
            />
          </div>
        </div>
        {cost !== defaultCost?
          <div style={{backgroundColor: "#dddddd"}}>
            <div style={{display: 'inline-block', width: '35%'}}>Cost:</div>
            <div style={{display: 'inline-block', width: '40%'}}></div>
            <div style={{display: 'inline-block', width: '25%', textAlign: 'right'}}>
              <MaskedNumber 
                displayType="text"
                value={cost} 
                prefix="$" 
                decimalScale={2} 
                fixedDecimalScale={true} 
              />
            </div>
          </div>
          : null
        }
        {cost !== defaultCost?
          <div style={{background: (cost - defaultCost) < 0? "#ffdddd" : "#eeeeee"}}>
            <div style={{display: 'inline-block', width: '35%'}}>Difference:</div>
            <div style={{display: 'inline-block', width: '40%'}}></div>
            <div style={{display: 'inline-block', width: '25%', textAlign: 'right'}}>
              <MaskedNumber 
                displayType="text"
                value={cost - defaultCost} 
                prefix={(cost - defaultCost) < 0? "$(" : "$"} 
                suffix={(cost - defaultCost) < 0? ")" : ""} 
                decimalScale={2} 
                fixedDecimalScale={true} 
                style={{color: (cost - defaultCost) < 0? "#bb0000" : "#202020"}}
              />
            </div>
          </div>
          : null          
        }
      </>
    } else {
      return <>
        {costDetails}
        <div style={{borderTop: "1px solid #dddddd"}}>
          <div style={{display: 'inline-block', width: '35%'}}>Cost:</div>
          <div style={{display: 'inline-block', width: '40%'}}></div>
          <div style={{display: 'inline-block', width: '25%', textAlign: 'right'}}>
            <MaskedNumber 
              displayType="text"
              value={cost} 
              prefix="$" 
              decimalScale={2} 
              fixedDecimalScale={true} 
            />
          </div>
        </div>
        {cost !== defaultCost?
          <div style={{background: (cost - defaultCost) < 0? "#ffdddd" : "#eeeeee"}}>
            <div style={{display: 'inline-block', width: '35%'}}>Difference:</div>
            <div style={{display: 'inline-block', width: '40%'}}></div>
            <div style={{display: 'inline-block', width: '25%', textAlign: 'right'}}>
              <MaskedNumber 
                displayType="text"
                value={cost - defaultCost} 
                prefix={(cost - defaultCost) < 0? "$(" : "$"} 
                suffix={(cost - defaultCost) < 0? ")" : ""} 
                decimalScale={2} 
                fixedDecimalScale={true} 
                style={{color: (cost - defaultCost) < 0? "#bb0000" : "#202020"}}
              />
            </div>
          </div>
          : null          
        }
      </>
    }
  }

  const costDetails = buildCostDetails(costBreakdown);

  const handleFieldClick = (event) => {
    if (readOnly) return;
    const valueEntryType = props.costCategory?.valueEntryType;
    if (valueEntryType === 'NONE') return;
    setAnchorEl(event.currentTarget);
    setFieldState({...fieldState, editing: true, selected, changed, quantity, cost, costBreakdown, recalculating: false, type});
  }

  const recalcCost = __.debounce((params) => {
    const {quantity, inboundCost, outboundCost, cost, useCost} = params;

    const overrides = {};
    if (quantity !== undefined) {
      overrides['overrideQuantity'] = quantity;
      overrides['valueEntryType'] = valueEntryType;    
      overrides['useCost'] = useCost;  
    }

    if (cost !== undefined && (useCost || valueEntryType === 'COST' || !valueEntryType)) {
      overrides['overrideCost'] = cost;
      overrides['overrideCostInbound'] = inboundCost;
      overrides['overrideCostOutbound'] = outboundCost;
    }

    initialiseAirportCost(queryClient, costCategory, [], aircraftReg, sector, cargoData, overrides, costingDate).then(
      (data) => {
        const {cost, defaultCostBreakdown, totalInboundCost, totalOutboundCost, airportCostFound, error} = data;
        setFieldState({...fieldState, changed: true, cost, quantity, costBreakdown: defaultCostBreakdown, recalculating: false, totalInboundCost, totalOutboundCost, error, airportCostFound, useCost});  
      }
    );
  }, 300, {leading: false, trailing: true});

  const handleChange = (value, type) => {
    let {totalInboundCost: inboundCost, totalOutboundCost: outboundCost, cost, useCost, quantity, defaultInboundCost, defaultOutboundCost} = fieldState;
    let changes = {inboundCost, outboundCost, cost, useCost, quantity, [type]: type === 'useCost' ? value : isNaN(value) ? 0 : value};
    if (type === 'useCost' && !Boolean(value ?? false)) {
      changes['inboundCost'] = defaultInboundCost;
      changes['outboundCost'] = defaultOutboundCost;
    }
    changes['cost'] = (changes['outboundCost'] || 0) + (changes['inboundCost'] || 0);
    console.debug('cost change', changes);

    recalcCost(changes);
  }

  const handleClosePopover = () => {
    setAnchorEl(null);
    setFieldState({...fieldState, editing: false});
  };

  const handleSelectedChanged = (selected) => {
    if (selected) {
      setFieldState({...fieldState, changed: true, selected, quantity: defaultQuantity, cost: defaultCost, totalInboundCost: defaultInboundCost, totalOutboundCost: defaultOutboundCost, costBreakdown: defaultCostBreakdown});
    } else {
      setFieldState({...fieldState, changed: selected !== defaultSelected, selected, quantity: 0, cost: 0, totalInboundCost: 0, totalOutboundCost: 0, costBreakdown: defaultCostBreakdown});
    }
  }
  const handleResetPressed = () => {
    setFieldState({...fieldState, changed: false, selected: defaultSelected, quantity: defaultQuantity, cost: defaultSelected?defaultCost:0, totalInboundCost: defaultSelected?defaultInboundCost:0, totalOutboundCost: defaultSelected?defaultOutboundCost:0, costBreakdown: defaultCostBreakdown, useCost: false});
  }
  const handleSavePressed = () => {
    if (onChange) onChange(index, {...__.pick(fieldState, ['changed', 'quantity', 'cost', 'costBreakdown', 'selected', 'totalInboundCost', 'totalOutboundCost', 'useCost'])});
    handleClosePopover();
  }
  const handleCancelPressed = () => {
    handleClosePopover();
  }

  const renderedCost = (cost?<MaskedNumber displayType="text" value={cost} prefix="$" decimalScale={2} fixedDecimalScale={true} />:!selected && defaultCost?<MaskedNumber style={{color: '#888888', fontStyle: 'italic', fontSize: '12px'}} displayType="text" value={defaultCost} prefix="$" decimalScale={2} fixedDecimalScale={true} />:<span>-</span>)


  if (fieldState.editing) {
    return (
      <div>
        <div style={{width: 'calc(100% - 16px)', height:'calc(100% - 16px)', padding: 8}} onClick={handleFieldClick}>
          {renderedCost}
        </div>
        <Popover
          open={true}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}>
          <div style={{padding: 8}}>
            <MuiGrid container spacing={3}>
              <MuiGrid item xs={2}>
                <CheckboxEditor
                  id="selected"
                  label="Include"
                  value={fieldState.selected}
                  onChange={handleSelectedChanged}
                  style={{padding: 8}}
                />
              </MuiGrid>
              <MuiGrid item xs={7}>
                {field !== 'cost' && field !== 'costInbound' && field !== 'costOutbound'?
                <MuiGrid container spacing={1}>
                  <MuiGrid item xs={6}>
                    <MaskedNumber 
                      label={fieldName}
                      customInput={TextField}
                      value={fieldState.quantity} 
                      decimalScale={decimalPlaces} 
                      fixedDecimalScale={true} 
                      prefix={prefix}
                      suffix={suffix}
                      allowNegative={false}
                      InputLabelProps={{shrink: true}}
                      onChange={(value) => handleChange(value, 'quantity')}
                      // onChange={handleQuantityChanged}
                      disabled={!fieldState.selected}
                      ignorePropChanges={true}
                    />
                  </MuiGrid>
                  <MuiGrid item xs={6}>
                    <CheckboxEditor
                      id="useCost"
                      label="Set Costs"
                      value={fieldState.useCost}
                      onChange={(value) => handleChange(value, 'useCost')}
                      style={{padding: 8}}
                    />
                  </MuiGrid>  
                </MuiGrid>
                : null}
                <div>
                  {type !== 'START' && (field === 'cost' || field === 'costInbound' || fieldState.useCost === true)?
                    <div style={{display: 'inline-block'}}>
                      {filtersEnabled && <div style={{display: 'inline-block', width: 24, position: 'relative', top: 23}}><Checkbox style={{padding: 0}} checked={filters?.in} onChange={(event) => onFilterChanged(costCategory.costCategoryUuid, index, 'in', event.target.checked)}/></div>} 
                      <div style={{display: 'inline-block'}}>
                        <MaskedNumber 
                          label="Inbound Cost"
                          customInput={TextField}
                          value={selected?fieldState.totalInboundCost:defaultInboundCost} 
                          decimalScale={2} 
                          fixedDecimalScale={true} 
                          prefix={"$"}
                          InputLabelProps={{shrink: true}}
                          onChange={(value) => handleChange(value, 'inboundCost')}
                          allowNegative={false}
                          // onChange={handleInboundCostChanged}
                          disabled={!fieldState.selected}
                          ignorePropChanges={true}
                        />
                      </div>
                    </div>
                  : null}
                  {type !== 'END' && (field === 'cost' || field === 'costOutbound' || fieldState.useCost === true)?
                    <div style={{display: 'inline-block', }}>
                      {filtersEnabled && <div style={{display: 'inline-block', width: 24, position: 'relative', top: 23}}><Checkbox style={{padding: 0}} checked={filters?.out} onChange={(event) => onFilterChanged(costCategory.costCategoryUuid, index, 'out', event.target.checked)}/></div>} 
                      <div style={{display: 'inline-block'}}>
                        <MaskedNumber 
                        label="Outbound Cost"
                        customInput={TextField}
                        value={selected?fieldState.totalOutboundCost:defaultOutboundCost} 
                        decimalScale={2} 
                        fixedDecimalScale={true} 
                        prefix={"$"}
                        allowNegative={false}
                        InputLabelProps={{shrink: true}}
                        onChange={(value) => handleChange(value, 'outboundCost')}
                        // onChange={handleOutboundCostChanged}
                        disabled={!fieldState.selected}
                        ignorePropChanges={true}
                      />
                      </div>
                    </div>
                  : null}
                </div>
              </MuiGrid>
              <MuiGrid item xs={3}>
                <div style={{marginTop: 20}}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    p: 1,
                    m: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    padding: 0,
                    margin: 0,
                    ...props.boxProps
                  }}>
                    <Tooltip title="Reset to default">
                      <IconButton
                        id="reset"
                        onClick={handleResetPressed}
                        style={{padding: 2}}
                        disabled={!fieldState.changed}
                      >
                        <Icons>replay</Icons>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Apply Change">
                      <IconButton
                        id="save"
                        onClick={handleSavePressed}
                        style={{padding: 2}}
                        disabled={JSON.stringify({quantity, cost, costBreakdown, selected, changed}) === JSON.stringify(__.pick(fieldState, ['quantity', 'cost','costBreakdown', 'selected', 'changed']))}
                      >
                        <Icons>check</Icons>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        id="cancel"
                        onClick={handleCancelPressed}
                        style={{padding: 2}}
                      >
                        <Icons>clear</Icons>
                      </IconButton>
                    </Tooltip>
                  </Box>  
                </div>
              </MuiGrid>
              <MuiGrid item xs={12}>
                <div style={{position: 'relative'}}>
                  {fieldState.recalculating ? 
                    <div style={{width: '100%', maxHeight: '100%', position: 'absolute', outline: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <CircularProgress/>
                    </div>                        
                  :null}
                  {renderCostDetails(fieldState.selected && field !== 'cost'?fieldState.costBreakdown:defaultCostBreakdown, fieldState.cost, defaultCost, field === 'cost')}
                </div>
              </MuiGrid>
            </MuiGrid>
          </div>
        </Popover>
      </div>
    )      
  }

  let showIndicator = false;
  let indicatorColor = "#FF0000";
  let indicatorTooltip = "";
  if (fieldState.error && !fieldState.changed) {
    if (!fieldState.error?.hasAirportCost) {
      showIndicator = true;
      indicatorColor = "#FF00FF";
      indicatorTooltip = "No Airport cost was found for this line, using default costing.";
    } else {
      if (fieldState.error?.validityPeriodMissing) {
        showIndicator = true;
        indicatorTooltip = "The airport costing has not been given a validity period.";
      } else if (fieldState.error?.validityExpired) {
        showIndicator = true;
        indicatorTooltip = `The airport costing expired on the ${moment(fieldState.error?.validTo).format('DD MMM YYYY')}`;        
      }
    }
  }

  if (costDetails) {
    return (
      <div style={{width: 'calc(100% - 16px)', height:'calc(100% - 16px)', padding: 8, position: "relative"}} onClick={handleFieldClick}>
        {showIndicator?<StatusIndicator color={indicatorColor} tooltip={indicatorTooltip} classes={classes}/>:null}
        <Tooltip classes={{tooltip: classes.costBreakdownTooltip}} title={costDetails}>
          <div>
            {renderedCost}
          </div>
        </Tooltip>
      </div>
    );  
  }
  return (
    <div style={{width: 'calc(100% - 16px)', height:'calc(100% - 16px)', padding: 8, position: "relative"}} onClick={handleFieldClick}>
        {showIndicator?<StatusIndicator color={indicatorColor} tooltip={indicatorTooltip} classes={classes}/>:null}
      {renderedCost}
  </div>
  );

}

const CostingRow = (props) => {
  const {costCategory, record, rebuilding, queryClient, sectorAirportsData, cargoData, onChange, classes, filtersEnabled, costFilters} = props;
  let {readOnly} = props;
  const {legs, services, aircraftReg, costingDate} = record;
  const service = __.find(services, {costCategoryUuid:costCategory.costCategoryUuid});
  const costRecordAirports = service?.costRecordAirports||[];

  const handleCostingChange = (index, changes) => {
    const airportCosts = costRecordAirports[index];
    costRecordAirports[index] = {...airportCosts, ...changes};
    service['costRecordAirports'] = [...costRecordAirports];
    if (onChange) {
      onChange({services});
    }
  }

  const categoryFilterState = (costCategoryUuid) => {
    const {groundCostDetails} = costFilters;
    let filterState = undefined;
    const {airports} = groundCostDetails;
    for (const airport of airports) {
      if (!airport) return {checked: false, indeterminate: true};
      if (filterState === undefined) filterState = airport[costCategoryUuid]['out'];
      if (filterState !== airport[costCategoryUuid]['out']) filterState = null;        
      if (filterState !== airport[costCategoryUuid]['in']) filterState = null;        
    }
    return {checked: filterState===true, indeterminate: filterState===null};
  }

  const toggleCategoryFilter = (costCategoryUuid, checked) => {
    const changes = {groundCostDetails: {...costFilters.groundCostDetails}};
    const {groundCostDetails} = changes;
    const {airports, categories} = groundCostDetails;
    for (const airport of airports) {
      categories[costCategoryUuid] = checked;
      airport[costCategoryUuid]['out'] = checked;
      airport[costCategoryUuid]['in'] = checked;
    }

    onChange({costFilters: {...costFilters, ...changes}});
  }

  if (rebuilding) {
    const primaryLegs = Array.isArray(legs) ? __.filter(legs, {transportType: "PRIMARY"}) : [];
    const sectorAirports = primaryLegs.length > 0?[primaryLegs[0].originAirportCode, ...primaryLegs.map(leg => leg.destinationAirportCode)]:[];

    return (
      <TableRow>
        <TableCell style={{fontWeight: 700}}>{filtersEnabled && <div style={{display: 'inline-block', width: 28, height: 16}}>{filtersEnabled && <Checkbox style={{width: 16, height: 16, padding: 0}} {...categoryFilterState(costCategory.costCategoryUuid)} onChange={(e) => toggleCategoryFilter(costCategory.costCategoryUuid, e.target.checked)}/>}</div>}<div style={{display: 'inline-block', height: 16, verticalAlign: 'top'}}>{costCategory.costCategoryName}</div></TableCell>
        {sectorAirports.map((sector, i) => {
          return (
            <TableCell style={{backgroundColor: i%2===0?"#f0f0f0":"#ffffff"}} width="80px" align="right"><span>--</span></TableCell>
          )
        })}
        <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><span>--</span></TableCell>
      </TableRow>
    )
  }

  const extraStyle = {};
  const opacityStyle = {};
  if (!service) {
    extraStyle.backgroundColor = "#f0f0f0";
    extraStyle.color = "#dd0000";
    opacityStyle.opacity = "0.6";
    readOnly = true;
  }

  const handleFilterChanged = (costCategoryUuid, airportIndex, direction, checked) => {
    const changes = {groundCostDetails: {...costFilters.groundCostDetails}};
    const {groundCostDetails} = changes;
    const {airports} = groundCostDetails;
    airports[airportIndex][costCategoryUuid][direction] = checked;
    onChange({costFilters: {...costFilters, ...changes}});
  }

  return (
    <TableRow style={{...opacityStyle}}>
      {service ?
        <TableCell style={{fontWeight: 700, padding: 0, ...extraStyle }}><div style={{display: 'inline-block', width: 8}}></div>{filtersEnabled && <div style={{display: 'inline-block', width: 28, height: 16}}>{filtersEnabled && <Checkbox style={{width: 16, height: 16, padding: 0}} {...categoryFilterState(costCategory.costCategoryUuid)} onChange={(e) => toggleCategoryFilter(costCategory.costCategoryUuid, e.target.checked)}/>}</div>}<div style={{display: 'inline-block', height: 16, verticalAlign: 'top'}}>{costCategory.costCategoryName}</div></TableCell>
      :
        <TableCell style={{fontWeight: 700, padding: 0, ...extraStyle }}>
          <Tooltip placement="bottom-start" title="This cost category was added after this costing was created, to be able to use it, please recalculate the costing.">
          <div><div style={{display: 'inline-block', width: 8}}></div><div style={{display: 'inline-block', height: 16, verticalAlign: 'top'}}>{costCategory.costCategoryName}</div></div>
          </Tooltip>
        </TableCell>
      }
      {sectorAirportsData.map((sector, i) => {
        const airportCosting = costRecordAirports[i];
        const airport = costFilters.groundCostDetails.airports[i];
        const filters = airport ? {out: airport[costCategory.costCategoryUuid]['out'], in: airport[costCategory.costCategoryUuid]['in']} : {out: true, in: true};
        if (!service) {
          return (
            <Tooltip title="This cost category was added after this costing was created, to be able to use it, please recalculate the costing.">
              <TableCell style={{color: i%2===0?"#000000":"rgba(0,0,0,0.87)", backgroundColor: i%2===0?"#f0f0f0":"#ffffff", padding: 0 }} width="80px" align="right"><span>--</span></TableCell>
            </Tooltip>
          )
        }
        let backgroundColor = i%2===0?"#f0f0f0":"#ffffff";
        if (airportCosting?.changed) {
          backgroundColor = (i%2===0?"#d0d0ff":"#e0e0ff");
        }
        if (i === 0 && !filters.out) {
          backgroundColor = (i%2===0?"#f0c0c0":"#f0b0b0");
        } else if (i === sectorAirportsData.length - 1 && !filters.in) {
          backgroundColor = (i%2===0?"#f0c0c0":"#f0b0b0");
        } else if (!filters.out && !filters.in) {
          backgroundColor = (i%2===0?"#f0c0c0":"#f0b0b0");
        } else if (!filters.out || !filters.in) {
          backgroundColor = (i%2===0?"#ffd580":"#efc570");
        }
        return (
          <TableCell style={{color: i%2===0?"#000000":"rgba(0,0,0,0.87)", backgroundColor: backgroundColor, padding: 0 }} width="80px" align="right"><CostingField index={i} airportCosting={airportCosting} costCategory={costCategory} aircraftReg={aircraftReg} queryClient={queryClient} sector={sector} cargoData={cargoData} onChange={handleCostingChange} readOnly={readOnly} classes={classes} costingDate={costingDate} filtersEnabled={filtersEnabled} filters={filters} onFilterChanged={handleFilterChanged}/></TableCell>
        )
      })}
      <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700, padding: 0}} width="100px" align="right"><div style={{padding: 8}}>{service && getCostRecordAirportsTotal(service, costFilters)}</div></TableCell>
    </TableRow>
  )
}

const CostingGroup = (props) => {
  const {groupName, record} = props;
  const primaryLegs = Array.isArray(record.legs) ? __.filter(record.legs, {transportType: "PRIMARY"}) : [];
  const legCount = primaryLegs?.length||-1;
  return (
    <TableRow key={groupName} style={{borderBottom: "2px solid #909090"}}>
      <TableCell colSpan={legCount+3} style={{padding: 2, textAlign: "left", fontSize: "10px", fontWeight: 700}}>{groupName}</TableCell>
    </TableRow>
  )
}

const CostingTotals = (props) => {
  const {record} = props;
  const services = record.services||[];
  const primaryLegs = Array.isArray(record.legs) ? __.filter(record.legs, {transportType: "PRIMARY"}) : [];

  let costingGrandTotal = 0;
  const legCount = primaryLegs?.length||-1;
  let costingTotals = new Array(legCount + 1).fill(0, 0, legCount + 1);

  // TODO: Need to account for filters
  const costFilters = record?.costFilters||{};
  const airportFilters = costFilters?.groundCostDetails?.airports||[];
  if (services) {
    services.forEach(service => {
      const {costCategoryUuid} = service;
      service.costRecordAirports.forEach((costing, i) => {
        let inboundFilter = true;
        let outboundFilter = true;
        if (airportFilters && airportFilters.length> i) {
          const airport = airportFilters[i];
          if (airport) {
            if (__.has(airport, costCategoryUuid)) inboundFilter = airport[costCategoryUuid].in;
            if (__.has(airport, costCategoryUuid)) outboundFilter = airport[costCategoryUuid].out;
          }
        }
        if (inboundFilter) {
          costingTotals[i] += costing.totalInboundCost||0;
          costingGrandTotal += costing.totalInboundCost||0;
        }
        if (outboundFilter) {
          costingTotals[i] += costing.totalOutboundCost||0;
          costingGrandTotal += costing.totalOutboundCost||0;
        }
      })
    })
  }

  return (
    <TableRow>
      <TableCell style={{backgroundColor: "#f0f0f0", fontWeight: 700}}>Station Totals</TableCell>
      {costingTotals.map((total, i) => (
        <TableCell style={{backgroundColor: i%2===0?"#d0d0d0":"#d9d9d9", fontWeight: 700}} width="80px" align="right">{total?<MaskedNumber displayType="text" value={total} prefix="$" decimalScale={2} fixedDecimalScale={true} />:<span>-</span>}</TableCell>
      ))}
      <TableCell style={{backgroundColor: "#c9c9c9", fontWeight: 700}} width="100px" align="right">{costingGrandTotal?<MaskedNumber displayType="text" value={costingGrandTotal} prefix="$" decimalScale={2} fixedDecimalScale={true} />:<span>-</span>}</TableCell>
    </TableRow>
  );
}

const CostingGroundEditor = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const {record, systemData: SystemData, classes, eventBus, rebuildRunning, readOnly, filtersEnabled} = props;

  const queryClient = useQueryClient();
  const costCategories = useCostCategories({queryClient, extraKeys: [], params: {includeGroup: 1}})

  const sectorAirports = Array.isArray(record?.legs) && __.filter(record.legs, {transportType: "PRIMARY"}).length > 0?[__.filter(record.legs, {transportType: "PRIMARY"})[0].originAirportCode, ...__.filter(record.legs, {transportType: "PRIMARY"}).map(leg => leg.destinationAirportCode)]:[];
  const {sectorAirportsData} = buildSectorsAirportData(record?.legs);

  const cargoData = getCargoData(record.legs, record.cargo);

  const costFilters = {
    ...defaultCostFilters,
    ...(record?.costFilters || {})
  };

  const onChange = (changes) => {
    if (props.onChange) {
      props.onChange({...changes});
    }
  }
  
  const groundFilterState = () => {
    const {groundCostDetails} = costFilters;
    let filterState = undefined;
    const {airports} = groundCostDetails;
    for (const airport of airports) {
      const categoryFilters =__.omit(airport, ['in', 'out', 'airportCode', 'enabled']);
      for (const categoryFilter in categoryFilters) {
        if (!costCategories.data || !__.find(costCategories.data, {costCategoryUuid: categoryFilter})) continue;
        if (filterState === undefined) filterState = categoryFilters[categoryFilter]['out'];
        if (filterState !== categoryFilters[categoryFilter]['out']) filterState = null;
        if (filterState !== categoryFilters[categoryFilter]['in']) filterState = null;        
      }      
    }
    return {checked: filterState===true, indeterminate: filterState===null};
  }
  const toggleGroundFilter = (checked) => {
    const changes = {groundCosts: checked, groundCostDetails: {...costFilters.groundCostDetails}};
    const {groundCostDetails} = costFilters;
    const {airports} = groundCostDetails;
    for (const airport of airports) {
      airport['in'] = checked;
      airport['out'] = checked;      
      const categoryFilters =__.omit(airport, ['in', 'out', 'airportCode', 'enabled']);
      for (const categoryFilter in categoryFilters) {
        if (!costCategories.data || !__.find(costCategories.data, {costCategoryUuid: categoryFilter})) continue;
        airport[categoryFilter]['out'] = checked;
        airport[categoryFilter]['in'] = checked;
      }      
    }

    onChange({costFilters: {...costFilters, ...changes}});
  }

  const airportFilterState = (airportCode, airportIndex, direction) => {
    const {groundCostDetails} = costFilters;
    let filterState = undefined;
    const airport = groundCostDetails.airports[airportIndex];
    if (!airport) return {checked: false, indeterminate: true};
    if (!airport.airportCode || airport.airportCode !== airportCode) return {checked: false, indeterminate: true};
    const categoryFilters =__.omit(airport, ['in', 'out', 'airportCode', 'enabled']);
    for (const categoryFilter in categoryFilters) {
      if (!costCategories.data || !__.find(costCategories.data, {costCategoryUuid: categoryFilter})) continue;
      if (filterState === undefined) filterState = categoryFilters[categoryFilter][direction];
      if (filterState !== categoryFilters[categoryFilter][direction]) filterState = null;        
    }      
    return {checked: filterState===true, indeterminate: filterState===null};
  }

  const toggleAirportFilter = (airportCode, airportIndex, direction, checked) => {
    const changes = {groundCostDetails: {...costFilters.groundCostDetails}};
    const {groundCostDetails} = costFilters;
    const {airports} = groundCostDetails;
    const airport = airports[airportIndex];
    if (!airport.airportCode || airport.airportCode !== airportCode) return;
    airport[direction] = checked;
    const categoryFilters =__.omit(airport, ['in', 'out', 'airportCode', 'enabled']);
    for (const categoryFilter in categoryFilters) {
      if (!costCategories.data || !__.find(costCategories.data, {costCategoryUuid: categoryFilter})) continue;
      airport[categoryFilter][direction] = checked;
    }      

    onChange({costFilters: {...costFilters, ...changes}});
  }


  if (costCategories.data && costFilters && record) {
    return (
      <div key="CostingGroundEditorContainer" style={{width: "100%", textAlign:"left" }}>
          <Grid containerspacing={3}>
            <Grid item xs={12} row>
              <LabelledBox label="" >
                <FormControl key="FC-addresses" >
                  <FormGroup key="FG-addresses">
                    <SectionHeading>Ground Costs</SectionHeading>
                    <Table className={classes.groundCosts}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{filtersEnabled && <div style={{display: 'inline-block', width: 28, height: 16}}><Checkbox style={{width: 16, height: 16, padding: 0}} {...groundFilterState()} onChange={(event) => toggleGroundFilter(event.target.checked)}/></div>}<div style={{display: 'inline-block', height: 16, verticalAlign: 'top'}}>Cost Type</div></TableCell>
                          {sectorAirports.map((airportCode, i) => (
                            <TableCell width="80px" align="center" padding="none">{filtersEnabled && <div style={{float: 'left', display: 'inline-block', width: 24, height: 16}}>{i !== 0 && <Checkbox style={{width: 16, height: 16, padding: 0}} {...airportFilterState(airportCode, i, 'in')} onChange={(event) => toggleAirportFilter(airportCode, i, 'in', event.target.checked)}/>}</div>}<div style={{display: 'inline-block', height: 16, verticalAlign: 'top'}}>{airportCode}</div>{filtersEnabled && <div style={{float: 'right', display: 'inline-block', width: 24, height: 16}}>{i < sectorAirports.length - 1 && <Checkbox style={{width: 16, height: 16, padding: 0}} {...airportFilterState(airportCode, i, 'out')} onChange={(event) => toggleAirportFilter(airportCode, i, 'out', event.target.checked)}/>}</div>}</TableCell>
                          ))}
                          <TableCell width="100px" align="right">Totals</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {costCategories.data.map((costCategory, i) => {
                          let group = null;
                          if (i === 0 || (i > 0 && costCategories.data[i - 1].costingGroupUuid !== costCategory.costingGroupUuid)) {
                            group = (<CostingGroup groupName={costCategory.groupName} record={record} key={costCategory.costingGroupUuid} />);
                          }
                          return (  
                            <>
                              {group}
                              <CostingRow costCategory={costCategory} record={record} rebuilding={rebuildRunning} queryClient={queryClient} sectorAirportsData={sectorAirportsData} cargoData={cargoData} onChange={onChange} readOnly={readOnly} classes={classes} filtersEnabled={filtersEnabled} costFilters={costFilters}/>
                            </>
                          )
                        })}
                        <CostingTotals record={record}/>
                      </TableBody>
                    </Table>
                  </FormGroup>
                </FormControl>
              </LabelledBox>
            </Grid>
          </Grid>
      </div>
    );
  }
  return (<></>);
}

export default CostingGroundEditor;
